import { useState } from "react";
import { isMockMode } from "../../../api/client";
import { AMZ_IMAGE_ATTRS } from "../../../types/amazon";
import type { AmzProductDetail } from "../../../types/amazon";
import { fileNameOf, getImages, imageSlotName, setImages } from "../../../lib/amazonContent";
import { Badge } from "../AmzUi";
import { LibraryPicker } from "../LibraryPicker";
import type { ProductEditor } from "../useProductEditor";

interface ImagesTabProps {
  detail: AmzProductDetail;
  editor: ProductEditor;
  showToast?: (msg: string) => void;
}

function Preview({ url }: { url: string | null }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="amz-img-preview">
      {url && !failed && !isMockMode() && <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} />}
      <span>{url ? "画像URL" : "未設定"}</span>
    </div>
  );
}

export function ImagesTab({ detail, editor, showToast }: ImagesTabProps) {
  const urls = getImages(editor.draft);
  const curUrls = detail.current ? getImages(detail.current) : null;
  const [drag, setDrag] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const mp = detail.marketplaceId;
  const max = AMZ_IMAGE_ATTRS.length;

  const commit = (next: string[]) => editor.edit((d) => setImages(d, next, mp), ["images"]);

  const move = (from: number, to: number) => {
    const next = [...urls];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    commit(next);
  };

  return (
    <>
      <div className="amz-note-bar">
        <span>ドラッグで順序を変更できます。URLの編集・順序の変更も下書きとして扱われます。</span>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="amz-btn" onClick={() => setPickerOpen(true)}>
            画像ライブラリから追加
          </button>
          <button type="button" className="amz-btn outline" disabled title="準備中">
            AI画像レビュー
          </button>
        </div>
      </div>
      <div className="amz-img-grid">
        {urls.map((url, i) => {
          const moved = curUrls !== null && curUrls[i] !== url;
          return (
            <div
              key={`${i}-${url}`}
              className={`amz-img-card ${over === i && drag !== null && drag !== i ? "over" : ""}`}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                try {
                  e.dataTransfer.setData("text/plain", String(i));
                } catch {
                  /* 一部ブラウザは setData が失敗する */
                }
                setDrag(i);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (over !== i) setOver(i);
              }}
              onDrop={(e) => {
                e.preventDefault();
                const from = drag;
                setDrag(null);
                setOver(null);
                if (from !== null && from !== i) move(from, i);
              }}
              onDragEnd={() => {
                setDrag(null);
                setOver(null);
              }}
            >
              <div className="amz-img-slot">
                <b className={i === 0 ? "is-main" : ""}>{imageSlotName(i)}</b>
                <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {moved && (
                    <Badge tone="accent" size="xs">
                      移動
                    </Badge>
                  )}
                  <button
                    type="button"
                    className="amz-ai-close"
                    style={{ fontSize: 16 }}
                    title="このスロットを空にする"
                    aria-label={`${imageSlotName(i)} を削除`}
                    onClick={() => commit(urls.filter((_, j) => j !== i))}
                  >
                    ×
                  </button>
                </span>
              </div>
              <Preview url={url} />
              <span className="amz-img-file" title={url}>
                {fileNameOf(url)}
              </span>
              <input
                className="amz-img-url"
                defaultValue={url}
                aria-label={`${imageSlotName(i)} のURL`}
                draggable={false}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== url) commit(urls.map((u, j) => (j === i ? v : u)));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
              />
            </div>
          );
        })}
        {urls.length < max && (
          <div className="amz-img-card empty">
            <div className="amz-img-slot">
              <b>{imageSlotName(urls.length)}</b>
            </div>
            <Preview url={null} />
            <input
              className="amz-img-url"
              placeholder="https://… 画像URLを追加（Enter）"
              aria-label="画像URLを追加"
              onKeyDown={(e) => {
                if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
                const t = e.target as HTMLInputElement;
                const v = t.value.trim();
                if (!v) return;
                commit([...urls, v]);
                t.value = "";
              }}
            />
          </div>
        )}
      </div>
      {pickerOpen && (
        <LibraryPicker
          sku={detail.sku}
          current={urls}
          max={max}
          onClose={() => setPickerOpen(false)}
          onApply={(next, message) => {
            commit(next);
            setPickerOpen(false);
            showToast?.(message);
          }}
        />
      )}
    </>
  );
}
